import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Countdown used to rate-limit the OTP "Resend code" button. Starts on mount
 * (a code was just sent) and can be restarted via `startCooldown` after each
 * resend. `canResend` is true only once the countdown reaches zero.
 */
export function useResendCooldown(seconds: number): {
	secondsRemaining: number;
	canResend: boolean;
	startCooldown: (overrideSeconds?: number) => void;
} {
	const [secondsRemaining, setSecondsRemaining] = useState(seconds);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clear = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	// `overrideSeconds` lets the caller re-arm the countdown to a server-supplied
	// window (e.g. eID Easy's "wait N seconds") instead of the default.
	const startCooldown = useCallback(
		(overrideSeconds?: number) => {
			clear();
			setSecondsRemaining(overrideSeconds ?? seconds);
			intervalRef.current = setInterval(() => {
				setSecondsRemaining((prev) => {
					if (prev <= 1) {
						clear();
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		},
		[seconds, clear]
	);

	useEffect(() => {
		startCooldown();
		return clear;
	}, [startCooldown, clear]);

	return { secondsRemaining, canResend: secondsRemaining === 0, startCooldown };
}
